import { createContext, useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// FR ↔ EN route mapping. Slugs of proper nouns (novel titles) stay identical.
export const ROUTE_MAP = {
  fr: {
    "/": "/en",
    "/auteur": "/en/author",
    "/romans": "/en/novels",
    "/cinema/protocole-zero": "/en/film/protocol-zero",
    "/actualites": "/en/news",
    "/journal-climat": "/en/climate-journal",
    "/articles": "/en/articles",
    "/presse": "/en/press",
    "/contact": "/en/contact",
    "/mentions-legales": "/en/legal-notice",
    "/confidentialite": "/en/privacy-policy",
    "/cookies": "/en/cookies",
    "/ecosysteme": "/en/ecosystem",
    "/editeurs-agents": "/en/publishers-agents",
  },
};
// Reverse map EN → FR built from ROUTE_MAP.fr
ROUTE_MAP.en = Object.fromEntries(
  Object.entries(ROUTE_MAP.fr).map(([fr, en]) => [en, fr])
);

// Allowlist of EN routes actually implemented today. Everything else in nav/footer
// referencing a route NOT in this set MUST be masked (per "aucun lien cassé" rule).
// Extend this list as each subsequent LOT (3B/3C/3D) delivers its pages.
export const EN_AVAILABLE_ROUTES = new Set([
  "/en",
  "/en/author",
  "/en/novels",
  "/en/film/protocol-zero",
  // Novel detail pages — one per slug (LOT 3B)
  "/en/novels/malo-les-routes-secretes-de-locean-indien",
  "/en/novels/malo-la-route-de-lest",
  "/en/novels/malo-la-route-du-soleil-levant",
  "/en/novels/terra-australis",
  "/en/novels/les-chaines-du-vivant-tome-1-neuroaurum",
  "/en/novels/les-chaines-du-vivant-les-seuils-interieurs",
  "/en/novels/les-chaines-du-vivant-tome-3",
  "/en/novels/mauritia-le-peuple-de-l-eau",
  "/en/novels/larbre-qui-cachait-la-foret",
  "/en/novels/le-feu-interieur",
  "/en/novels/projet-lithium-luminia",
  "/en/novels/les-heritages-du-vivant",
  "/en/novels/elle",
  "/en/novels/l-homme-providentiel",
  "/en/novels/meadows-t1-dereglement-climatique",
  "/en/novels/meadows-t2-last-limit",
  "/en/novels/lepave-daksoum-lodyssee-des-minots-de-marseille",
  "/en/novels/machina-sapiens",
  // First-chapters EN pages (LOT 3B.1)
  "/en/novels/malo-les-routes-secretes-de-locean-indien/first-chapters",
  "/en/novels/malo-la-route-du-soleil-levant/first-chapters",
  "/en/novels/les-chaines-du-vivant-tome-1-neuroaurum/first-chapters",
  "/en/novels/terra-australis/first-chapters",
  // News EN (LOT 3C) — list is public. Detail routes are per-slug; only slugs whose
  // body has been fully translated in `locales/en/news.json` render a full EN page.
  "/en/news",
  "/en/news/bienvenue-dans-le-journal-de-bord",
  "/en/news/terra-australis-sous-la-glace-une-memoire-attend",
  "/en/news/mauritia-un-monde-repris-par-l-ocean",
  "/en/news/malo-les-routes-secretes-de-locean-indien-dossier-de-presentation-disponible",
  "/en/news/ouverture-du-site-officiel-de-bruno-courtin",
  // LOT EN-PUBLIC.1 — 14 news article bodies translated to EN
  "/en/news/elle-une-presence-silencieuse-au-coeur-d-une-vie",
  "/en/news/projet-lithium-luminia-quand-la-terre-garde-la-memoire",
  "/en/news/malo-tome-ii-la-route-de-l-est",
  "/en/news/recherches-en-cours-sur-terra-australis",
  "/en/news/neuroaurum-la-memoire-comme-champ-de-bataille",
  "/en/news/l-arbre-qui-cachait-la-foret-ecouter-le-vivant-autrement",
  "/en/news/le-feu-interieur-transformer-ce-qui-brule",
  "/en/news/civilisations-anciennes-ne-pas-confondre-absence-et-inexistence",
  "/en/news/le-climat-comme-personnage-invisible",
  "/en/news/dans-les-coulisses-des-romans",
  "/en/news/les-heritages-du-vivant-terre-eau-air-et-feu",
  "/en/news/civilisation-maya-matiere-roman-a-venir",
  "/en/news/malo-tome-iii-la-route-du-soleil-levant",
  "/en/news/rechauffement-climatique-france-2026",
  // Mini Climate Journal (Phase 9 EN)
  "/en/climate-journal",
  // Contact EN (LOT 3C)
  "/en/contact",
  // LOT 3D — Press, Publishers, Ecosystem
  "/en/press",
  "/en/publishers-agents",
  "/en/ecosystem",
  // Institutional (LOT 3A)
  "/en/legal-notice",
  "/en/privacy-policy",
  "/en/cookies",
]);

export function isEnRouteAvailable(path) {
  if (!path) return false;
  const p = path.length > 1 && path.endsWith("/") ? path.slice(0, -1) : path;
  return EN_AVAILABLE_ROUTES.has(p);
}

// Canonical locale resolver. Not exported directly to keep components locale-agnostic.
// Rules:
//   • "/en"          → en
//   • "/en/"         → en
//   • "/en/anything" → en
//   • everything else → fr (default)
function resolveLocale(rawPathname = "/") {
  // Strip trailing slash except for root
  const p = rawPathname.length > 1 && rawPathname.endsWith("/")
    ? rawPathname.slice(0, -1)
    : rawPathname;
  if (p === "/en" || p.startsWith("/en/")) return "en";
  return "fr";
}

const LocaleContext = createContext({ locale: "fr", switchTo: () => {} });

export function LocaleProvider({ children }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const locale = resolveLocale(pathname);

  const value = useMemo(() => ({
    locale,
    switchTo(targetLocale) {
      if (targetLocale === locale) return;
      // Normalise trailing slash for lookups
      const p = pathname.length > 1 && pathname.endsWith("/")
        ? pathname.slice(0, -1)
        : pathname;
      // Preserve URL search params across language switches. LOT E1 requires
      // the pro workspace's ?section= and ?novel= to survive the switch so the
      // user keeps the same section/novel selected. Applies globally — cheap
      // and correct for every route.
      const suffix = window?.location?.search || "";

      if (targetLocale === "en") {
        // If the direct mapping targets an EN page that doesn't exist yet, fall
        // back explicitly to /en (home) rather than silently keeping the user on
        // a broken/blank EN route or on the FR page.
        const direct = ROUTE_MAP.fr[p];
        if (direct && EN_AVAILABLE_ROUTES.has(direct)) return navigate(direct + suffix);
        // Prefix-map dynamic routes: /romans/xxx → /en/novels/xxx (Terra Australis today)
        if (p.startsWith("/romans/")) {
          const candidate = "/en/novels/" + p.slice(8);
          if (EN_AVAILABLE_ROUTES.has(candidate)) return navigate(candidate + suffix);
        }
        return navigate("/en");
      }
      // en → fr
      const direct = ROUTE_MAP.en[p];
      if (direct) return navigate(direct + suffix);
      if (p.startsWith("/en/novels/")) return navigate("/romans/" + p.slice(11) + suffix);
      if (p.startsWith("/en/news/")) return navigate("/actualites/" + p.slice(9) + suffix);
      if (p.startsWith("/en/articles/")) return navigate("/articles/" + p.slice(13) + suffix);
      return navigate("/");
    },
  }), [locale, pathname, navigate]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  return useContext(LocaleContext);
}

// Lightweight translation dict lookup with fallback to FR.
// Accepts { fr, en } dict shape. If a key is missing in `en`, we fall back to `fr` then to the raw key.
export function useT(dict) {
  const { locale } = useLocale();
  return (key) => {
    const src = dict?.[locale] || dict?.fr || {};
    return src[key] ?? dict?.fr?.[key] ?? key;
  };
}

// Exposed for tests / prerender.
export { resolveLocale };
